import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";
import {
    Inbox,
    RefreshCw,
    AlertTriangle,
    UserPlus,
    MessageSquare,
    PhoneIncoming,
    Mail,
    Clock,
    ChevronRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

/**
 * AdminInbox — "What needs my attention today?"
 *
 * Surfaces six event types from across the system in one stream:
 *   new_signup            · NEW SIGNUP   (medium-high, < 48h)
 *   welcome_failed        · MAIL FAIL    (high)
 *   incomplete_onboarding · STALLED      (medium, > 24h)
 *   change_request        · REQUEST      (high if submitted)
 *   lead                  · LEAD         (medium, < 48h)
 *   stalled_build         · BUILD STALL  (medium, > 7d)
 *
 * Each row links straight to the client where the admin can act.
 */
const TYPE_META = {
    new_signup: { label: "New signup", icon: UserPlus, color: "text-sky-300" },
    welcome_failed: { label: "Welcome failed", icon: Mail, color: "text-red-300" },
    incomplete_onboarding: { label: "Onboarding stalled", icon: Clock, color: "text-amber-300" },
    change_request: { label: "Change request", icon: MessageSquare, color: "text-violet-300" },
    lead: { label: "Lead", icon: PhoneIncoming, color: "text-emerald-300" },
    stalled_build: { label: "Build stalled", icon: AlertTriangle, color: "text-amber-300" },
};

const fmtSince = (iso) => {
    if (!iso) return "—";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
};

export default function AdminInbox() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | <type>

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/inbox");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load inbox");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => {
        const out = { all: items.length };
        items.forEach((i) => { out[i.type] = (out[i.type] || 0) + 1; });
        return out;
    }, [items]);

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        return items.filter((i) => i.type === filter);
    }, [items, filter]);

    return (
        <section className="mx-auto w-full max-w-5xl px-5 sm:px-8 pt-6 sm:pt-16 pb-40 md:pb-16" data-testid="admin-inbox-page">
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-950/60 text-[11.5px] tracking-tight text-zinc-400"
            >
                <Inbox className="h-3 w-3" />
                Action queue
            </motion.div>
            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-50"
            >
                What needs you today
            </motion.h1>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-zinc-400">
                New signups, failed welcome emails, stalled onboarding, open requests, fresh leads, stalled builds — all in one stream. Newest and highest priority first.
            </p>

            <div className="mt-8 sm:mt-10 flex items-center gap-2 flex-wrap">
                <Pill active={filter === "all"} onClick={() => setFilter("all")} testid="inbox-filter-all">
                    All <span className="ml-1.5 text-zinc-500">{counts.all}</span>
                </Pill>
                {Object.entries(TYPE_META).map(([type, meta]) => (
                    counts[type] ? (
                        <Pill key={type} active={filter === type} onClick={() => setFilter(type)} testid={`inbox-filter-${type}`}>
                            <meta.icon className={`h-3 w-3 mr-1 ${meta.color}`} />
                            {meta.label} <span className="ml-1.5 text-zinc-500">{counts[type]}</span>
                        </Pill>
                    ) : null
                ))}
                <button
                    type="button"
                    onClick={load}
                    aria-label="Refresh inbox"
                    data-testid="inbox-refresh-button"
                    className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {loading && <p className="mt-10 text-[13.5px] text-zinc-500" data-testid="inbox-loading">Loading…</p>}
            {!loading && filtered.length === 0 && (
                <p className="mt-10 text-[13.5px] text-zinc-500" data-testid="inbox-empty">
                    All clear. Nothing needs your attention right now.
                </p>
            )}

            <ul className="mt-6 space-y-2" data-testid="inbox-list">
                {filtered.map((it, idx) => (
                    <InboxItem key={`${it.type}-${it.tenant_id}-${idx}`} item={it} />
                ))}
            </ul>

            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: { background: "#0a0a0a", border: "1px solid #27272a", color: "#f4f4f5" },
                }}
            />
        </section>
    );
}

function Pill({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={[
                "h-8 px-3 rounded-full border text-[12.5px] inline-flex items-center transition-colors whitespace-nowrap",
                active
                    ? "bg-zinc-50 border-zinc-50 text-zinc-950"
                    : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

function InboxItem({ item }) {
    const meta = TYPE_META[item.type] || { label: item.type, icon: AlertTriangle, color: "text-zinc-300" };
    const Icon = meta.icon;
    const highPriority = item.priority === "high";

    // All actions route to the client card in /admin/clients
    const href = "/admin/clients";

    return (
        <li data-testid={`inbox-item-${item.type}-${item.tenant_id}`}>
            <Link
                to={href}
                className={[
                    "flex items-start gap-3 px-4 sm:px-5 py-3.5 rounded-xl border bg-zinc-950/40 hover:bg-zinc-950/70 transition-colors",
                    highPriority ? "border-amber-900/40" : "border-zinc-800/80",
                ].join(" ")}
            >
                <div className="shrink-0 mt-0.5">
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10.5px] uppercase tracking-[0.14em] ${meta.color}`}>
                            {meta.label}
                        </span>
                        {highPriority && (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-amber-300/90">· high</span>
                        )}
                        <span className="text-[11px] text-zinc-600">
                            · {fmtSince(item.created_at)}
                        </span>
                    </div>
                    <p className="mt-0.5 text-[13.5px] text-zinc-100 truncate">{item.headline}</p>
                    {item.detail && (
                        <p className="mt-0.5 text-[12px] text-zinc-500 truncate">{item.detail}</p>
                    )}
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0 mt-1" />
            </Link>
        </li>
    );
}
